/**
 * cee-content.ts
 * ─────────────────────────────────────────────────────────────────
 * Source unique du contenu éditorial de la page-guide
 * /particuliers/aides/cee (Prime énergie).
 *
 * RÈGLES ÉDITORIALES (AGENTS §8) :
 *  - Familles autorisées : BAR-TH-* (chauffage / ECS résidentiel),
 *    BAR-EN-* (enveloppe / isolation résidentielle), BAR-EQ-*
 *    (équipements résidentiels), BAR-SE-* (services résidentiels),
 *    BAR-TR-* (transports résidentiels).
 *  - PAS d'invention de codes fiches.
 *  - PAS de montants en € précis sans source.
 *  - Solution Éco France est intermédiaire agréé.
 */

// ════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════

export interface CeeSummaryPoint {
  text: string;
}

export interface CeeAteur {
  iconName: string;
  title: string;
  description: string;
  role: string;
}

export interface CeeFicheFamille {
  /** Code de la famille (BAR-TH, BAR-EN, BAR-EQ, BAR-SE, BAR-TR) */
  code: string;
  /** Libellé lisible */
  label: string;
  iconName: string;
  /** Description courte */
  description: string;
  /** Exemples d'opérations */
  examples: string[];
}

export interface CeeBonification {
  /** Nom du Coup de pouce (générique) */
  title: string;
  iconName: string;
  description: string;
  /** Public/typo d'opérations */
  target: string;
}

export interface CeeDemarche {
  step: number;
  iconName: string;
  title: string;
  description: string;
  /** Avertissement éventuel */
  warning?: string;
}

export interface CeeFaqItem {
  question: string;
  answer: string;
}

export interface CeeCumul {
  aide: string;
  iconName: string;
  description: string;
  tone: 'brand' | 'accent' | 'sage' | 'lavender' | 'ivory';
  href?: string;
}

// ════════════════════════════════════════════════════════════════
// HERO
// ════════════════════════════════════════════════════════════════

export const CEE_HERO = {
  eyebrow: 'Prime énergie',
  titlePrimary: 'Prime énergie',
  titleAccent: 'le dispositif expliqué simplement',
  lead:
    "La prime énergie est une aide versée par les fournisseurs d'énergie pour financer vos travaux. Sans condition de ressources, ouverte à tous, cumulable avec l'aide de l'État. Tout ce qu'il faut savoir.",
  amountHeadline: 'Sans condition de ressources',
  amountSubline:
    "Le montant dépend des travaux réalisés, de votre région et de la surface concernée. Notre simulateur vous donne une estimation à jour en quelques secondes.",
  microPromise: 'Cumulable avec toutes les aides. Accessible à tous depuis 2005.',
} as const;

export const CEE_SUMMARY: CeeSummaryPoint[] = [
  {
    text:
      "La prime énergie est une aide versée par les fournisseurs d'énergie, encadrée par l'État.",
  },
  {
    text:
      "Tout le monde y a droit, sans condition de ressources. Propriétaires occupants ou bailleurs, résidence principale ou secondaire.",
  },
  {
    text:
      "Le montant dépend des travaux, de votre région et de la surface concernée.",
  },
  {
    text:
      "Point essentiel : faites la demande de prime avant de signer le devis. Sinon, c'est refusé.",
  },
];

// ════════════════════════════════════════════════════════════════
// MÉCANISME — Comment ça marche
// ════════════════════════════════════════════════════════════════

export const CEE_MECANISME = {
  eyebrow: 'Comment ça marche',
  title: 'Le dispositif en deux mots',
  description:
    "L'État impose aux fournisseurs d'énergie de financer des économies d'énergie. Pour y arriver, ils vous aident à payer vos travaux. C'est ça, la prime énergie. Un système qui existe depuis 2005 et qui a déjà financé des millions de chantiers.",
} as const;

export const CEE_ACTEURS: CeeAteur[] = [
  {
    iconName: 'factory',
    title: 'Les financeurs',
    role: "Vendeurs d'énergie",
    description:
      "Fournisseurs d'électricité, de gaz, de fioul et de carburants (grands groupes énergétiques et enseignes de distribution). Ils doivent prouver des économies d'énergie chaque période et financent vos travaux pour atteindre leurs objectifs.",
  },
  {
    iconName: 'handshake',
    title: 'Les gestionnaires',
    role: 'Mandatés par les financeurs',
    description:
      "Acteurs spécialisés à qui les financeurs confient la gestion de leurs obligations. Ils instruisent les dossiers, contrôlent la qualité et déposent les dossiers sur le registre officiel.",
  },
  {
    iconName: 'shield-check',
    title: 'Les intermédiaires',
    role: 'Interface pour les artisans et les ménages',
    description:
      "Acteurs qui montent et déposent les dossiers de prime pour le compte d'artisans certifiés ou de ménages. Solution Éco France est intermédiaire agréé pour son réseau d'artisans partenaires et pour les artisans indépendants qui nous le demandent.",
  },
];

// ════════════════════════════════════════════════════════════════
// LES FAMILLES DE FICHES RÉSIDENTIELLES
// ════════════════════════════════════════════════════════════════

export const CEE_FAMILLES: CeeFicheFamille[] = [
  {
    code: 'BAR-TH',
    label: 'Chauffage & ECS',
    iconName: 'flame',
    description:
      "Opérations de chauffage et de production d'eau chaude sanitaire.",
    examples: [
      'Pompe à chaleur air-eau / géothermique',
      'Chauffe-eau thermodynamique',
      'Chauffe-eau solaire individuel',
      'Système solaire combiné',
      'Raccordement à un réseau de chaleur renouvelable',
      'Régulation et programmation du chauffage',
    ],
  },
  {
    code: 'BAR-EN',
    label: 'Enveloppe / Isolation',
    iconName: 'snowflake',
    description:
      "Travaux d'isolation thermique de l'enveloppe du bâti (murs, toiture, planchers, fenêtres).",
    examples: [
      'Isolation des combles perdus',
      'Isolation des rampants de toiture',
      'Isolation des murs (intérieur / extérieur)',
      'Isolation des planchers bas',
      'Remplacement de fenêtres / portes-fenêtres',
    ],
  },
  {
    code: 'BAR-EQ',
    label: 'Équipements',
    iconName: 'zap',
    description:
      "Équipements performants installés hors chauffage et hors enveloppe.",
    examples: [
      'VMC double flux',
      'Ventilation hygroréglable',
      'Éclairage LED résidentiel',
    ],
  },
];

// ════════════════════════════════════════════════════════════════
// COUPS DE POUCE — Bonifications
// ════════════════════════════════════════════════════════════════

export const CEE_BONIFICATIONS: CeeBonification[] = [
  {
    title: 'Bonus Chauffage',
    iconName: 'flame',
    target: "Remplacement d'un chauffage fossile",
    description:
      "Prime majorée pour remplacer une chaudière fioul, charbon ou gaz par une pompe à chaleur, une chaudière biomasse ou un raccordement à un réseau de chaleur.",
  },
  {
    title: 'Bonus Isolation',
    iconName: 'snowflake',
    target: 'Combles, toitures et planchers bas',
    description:
      "Prime majorée pour les travaux d'isolation des combles perdus, des rampants de toiture et des planchers bas.",
  },
  {
    title: 'Bonus Rénovation performante',
    iconName: 'layers',
    target: 'Rénovation globale (maisons individuelles)',
    description:
      "Prime majorée applicable aux rénovations globales permettant un gain énergétique important. Cumulable avec l'aide de l'État pour les rénovations d'ampleur.",
  },
];

// ════════════════════════════════════════════════════════════════
// CONDITIONS D'ÉLIGIBILITÉ
// ════════════════════════════════════════════════════════════════

export const CEE_CONDITIONS: string[] = [
  "Logement résidence principale ou secondaire achevé depuis plus de 2 ans",
  "Travaux éligibles correspondant à une opération du catalogue officiel",
  "Professionnel certifié pour la catégorie de travaux concernée",
  "Demande de prime faite avant la signature du devis",
  "Conservation des justificatifs : devis, facture, attestation, photos avant/après",
  "Réalisation des travaux conformément aux exigences techniques de la fiche",
];

// ════════════════════════════════════════════════════════════════
// DÉMARCHES — 5 ÉTAPES
// ════════════════════════════════════════════════════════════════

export const CEE_DEMARCHES: CeeDemarche[] = [
  {
    step: 1,
    iconName: 'calculator',
    title: 'Estimer le montant de votre prime',
    description:
      "On identifie l'opération qui correspond à vos travaux, votre région et la surface concernée. Le simulateur vous donne une estimation au prix du marché.",
  },
  {
    step: 2,
    iconName: 'file-signature',
    title: 'Choisir un partenaire qui vous versera la prime',
    description:
      "Vous restez libre de choisir l'acteur qui vous versera la prime. Avec Solution Éco France, deux options : prime déduite directement du devis via notre réseau d'artisans, ou nous portons votre dossier si vous gardez votre propre artisan.",
    warning:
      "Point essentiel : signez l'engagement de prime avant le devis. Sinon le dossier est refusé.",
  },
  {
    step: 3,
    iconName: 'hard-hat',
    title: 'Faire réaliser les travaux par un professionnel certifié',
    description:
      "L'artisan doit être certifié pour la catégorie de travaux concernée. Vérifiez sa certification avant signature.",
  },
  {
    step: 4,
    iconName: 'file-up',
    title: 'Constituer et déposer le dossier',
    description:
      "Devis, facture, attestation signée, photos avant/après selon l'opération. Le dossier est déposé sur le registre officiel par votre partenaire.",
  },
  {
    step: 5,
    iconName: 'wallet',
    title: 'Recevoir votre prime',
    description:
      "La prime est versée sous 30 à 60 jours après dépôt et validation. Trois modes possibles : virement bancaire, chèque, ou déduction directe sur le devis (réseau d'artisans partenaires).",
  },
];

// ════════════════════════════════════════════════════════════════
// CUMUL AVEC LES AUTRES AIDES
// ════════════════════════════════════════════════════════════════

export const CEE_CUMULS: CeeCumul[] = [
  {
    aide: "MaPrimeRénov'",
    iconName: 'piggy-bank',
    description:
      "Cumulable à 100 %. Le couple aide de l'État + prime énergie est la base de tout plan de financement pour la rénovation.",
    tone: 'brand',
    href: '/particuliers/aides/maprimerenov',
  },
  {
    aide: 'Éco-PTZ',
    iconName: 'banknote',
    description:
      "Prêt à taux zéro qui finance la part restant à votre charge. Cumulable avec la prime énergie sans aucune limitation.",
    tone: 'sage',
    href: '/particuliers/aides/eco-ptz',
  },
  {
    aide: 'TVA à 5,5 %',
    iconName: 'percent',
    description:
      "Le taux réduit s'applique automatiquement par l'artisan, indépendamment de la prime énergie. Aucune démarche supplémentaire.",
    tone: 'accent',
    href: '/particuliers/aides/tva-reduite',
  },
  {
    aide: 'Coup de pouce',
    iconName: 'flame',
    description:
      "Prime majorée pour les opérations ciblées (remplacement chauffage fossile, isolation, rénovation performante).",
    tone: 'lavender',
    href: '/particuliers/aides/coup-de-pouce',
  },
  {
    aide: 'Aides locales',
    iconName: 'landmark',
    description:
      "Régions, Départements, métropoles : aides complémentaires variables selon votre code postal.",
    tone: 'ivory',
  },
];

// ════════════════════════════════════════════════════════════════
// POURQUOI SEF — Intermédiaire agréé
// ════════════════════════════════════════════════════════════════

export const CEE_SEF_FEATURES: Array<{
  iconName: string;
  title: string;
  text: string;
}> = [
  {
    iconName: 'shield-check',
    title: 'Intermédiaire agréé',
    text: "Nous respectons toutes les obligations légales. Assurance responsabilité civile incluse.",
  },
  {
    iconName: 'sparkles',
    title: 'Prime déduite du devis',
    text: "Quand vous passez par notre réseau d'artisans, la prime est directement déduite. Vous ne faites pas l'avance.",
  },
  {
    iconName: 'handshake',
    title: 'Un seul interlocuteur',
    text: "Même équipe pour le diagnostic, le devis, le suivi et le versement. Pas de ping-pong entre différents services.",
  },
  {
    iconName: 'file-text',
    title: 'Dossier défendu',
    text: "Nous montons, déposons et défendons votre dossier. Si l'administration contrôle, c'est nous qui répondons.",
  },
];

// ════════════════════════════════════════════════════════════════
// FAQ
// ════════════════════════════════════════════════════════════════

export const CEE_FAQ: CeeFaqItem[] = [
  {
    question: 'Qui peut bénéficier de la prime énergie ?',
    answer:
      "Tous les ménages — propriétaires occupants, propriétaires bailleurs, locataires (avec accord du propriétaire), résidences principales ou secondaires. Aucune condition de ressources n'est exigée. C'est ce qui distingue la prime énergie de l'aide de l'État (résidence principale uniquement, plafonds de revenus).",
  },
  {
    question: 'Comment est calculé le montant de ma prime ?',
    answer:
      "Trois éléments entrent en jeu : l'opération concernée, votre région et la surface ou le type d'équipement. Le tarif des primes bouge avec le marché. Notre simulateur vous renvoie une estimation à jour.",
  },
  {
    question: "Qui sont les « financeurs » ?",
    answer:
      "Ce sont les vendeurs d'énergie (électricité, gaz, fioul, carburants) à qui l'État impose un volume d'économies d'énergie à réaliser. Pour atteindre leurs objectifs, ils financent vos travaux en échange de certificats.",
  },
  {
    question: "Pourquoi faut-il signer l'engagement avant le devis ?",
    answer:
      "Pour que la prime soit valable, vous devez prouver que c'est elle qui vous a incité à faire les travaux. Concrètement : l'engagement de prime doit dater d'avant la signature du devis avec l'artisan. Sinon, le dossier est rejeté. C'est la première cause de refus.",
  },
  {
    question: "La prime énergie est-elle cumulable avec l'aide de l'État ?",
    answer:
      "Oui, intégralement. Les deux dispositifs sont faits pour s'additionner. Sur un même chantier (par exemple une pompe à chaleur), vous touchez l'aide de l'État, la prime énergie, le prêt sans intérêt, la TVA à 5,5 % et les aides locales. Le cumul est plafonné à un pourcentage des dépenses selon votre profil.",
  },
  {
    question: "Suis-je obligé de passer par le fournisseur d'énergie dont je suis client ?",
    answer:
      "Non, absolument pas. Vous êtes totalement libre de choisir l'acteur qui vous versera la prime, sans lien avec votre contrat d'énergie. Vous pouvez passer par un autre fournisseur ou un intermédiaire — l'essentiel est de faire la demande avant le devis.",
  },
  {
    question: 'Comment Solution Éco France peut-elle m\'aider ?',
    answer:
      "Deux cas de figure. Avec notre réseau d'artisans partenaires, la prime énergie se déduit directement sur le devis : vous n'avancez rien. Si vous gardez votre propre artisan, il nous mandate pour son chantier et on porte le dossier de A à Z (engagement, montage, dépôt, contrôle, versement).",
  },
  {
    question: 'Et si mon dossier est refusé après dépôt ?',
    answer:
      "Sur les dossiers que nous portons, c'est Solution Éco France qui gère la défense du dossier. Nous vérifions la conformité documentaire avant dépôt pour éviter le risque de rejet : devis, factures, attestation, photos, certification en cours de validité au moment des travaux.",
  },
];
